import * as React from 'react';
import { ArrowLeftIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useRouter } from 'next/router';
import { useAtom, useAtomValue } from 'jotai';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import {
  Heading,
  Button,
  Modal,
  ModalContent,
  ModalConfirmContent,
  AccordionV2,
  JobUpdatedBadge,
} from '@restworld/ui-ds-base';
import { getAssetSrc, useModal } from '@restworld/utils-common';
import { LinkBase, PreInterviewBadge } from '@restworld/ui-components';

import {
  getActiveKeyAtom,
  getOpenKeysAtom,
  getCompletedKeysAtom,
  setOpenKeysAtom,
  getApplicationStateAtom,
  getIsSpontaneousApplicationAtom,
  applicationSentimentAndStatusAtom,
  getApplyJobStatusDataAtom,
} from './application-details.context';
import { TimeLineKeysType } from './application-details.constants';
import { useApplicationTimeline } from './use-application-timeline';
import { ApplicationDiscardButton, ApplicationInformationCard } from './components';
import { useApplicationReinstate, useCurrentApplicationStatus } from './application-details.hooks';
import { usePreInterview } from './use-pre-interview';
import { ExternalResourceCard } from '../../external-resource-card';
import { useJobApplicationCategoriesNavigation } from '../hooks/use-job-application-categories-navigation';
import { cn } from '@restworld/utils-common';
import { Alert } from '@restworld/ui-ds-base';
import { Banner } from '../../legacy';
import { JobNotFoundPage } from '../../legacy';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ICandidatureDetailsProps {}

export const JobApplicationDetails: React.FunctionComponent<ICandidatureDetailsProps> = (props) => {
  const { t } = useTranslation();
  const [applicationState] = useAtom(getApplicationStateAtom);
  const [isSpontaneousApplication] = useAtom(getIsSpontaneousApplicationAtom);
  const statusAndSentiment = useAtomValue(applicationSentimentAndStatusAtom);
  const router = useRouter();
  const { getJobCategoryNavigation } = useJobApplicationCategoriesNavigation();
  const jobApplicationCategory = React.useMemo(
    () =>
      getJobCategoryNavigation({
        statusKey: statusAndSentiment?.statusKey,
        statusSentiment: statusAndSentiment?.sentiment,
      }),
    [getJobCategoryNavigation, statusAndSentiment?.sentiment, statusAndSentiment?.statusKey]
  );
  // const { onRouterBack } = useRouterBackCallback('/candidature');
  const [applyJobStatusData] = useAtom(getApplyJobStatusDataAtom);
  // The one state where the page is not a status report but a request: the heading says so,
  // and goes back to today's "Ora puoi rilassarti!" as soon as the interview is closed.
  const isInterviewPending = usePreInterview()?.status === 'pending' && !isSpontaneousApplication;
  const isWithdrawnByWorker = applicationState === 'rejected' && applyJobStatusData?.discarded_by === 'worker';
  const wasSentToEmployer = !!applyJobStatusData?.added_to_shortlist_at;

  // "Offerta aggiornata" badge: the employer edited the listing after the worker
  // entered the shortlist (ROSA). We compare `added_to_shortlist_at` to the JP's
  // `last_material_update_at` — if the shortlist entry predates the edit, the
  // worker should know the listing has changed since they started being evaluated.
  const lastMaterialUpdateAt = applyJobStatusData?.job_position?.last_material_update_at;
  const addedToShortlistAt = applyJobStatusData?.added_to_shortlist_at;
  const showJpUpdatedBadge = React.useMemo(() => {
    if (!lastMaterialUpdateAt || !addedToShortlistAt) return false;
    return new Date(addedToShortlistAt).getTime() < new Date(lastMaterialUpdateAt).getTime();
  }, [lastMaterialUpdateAt, addedToShortlistAt]);

  const jpUpdatedBadge = showJpUpdatedBadge ? (
    <JobUpdatedBadge
      label={t('job-application-page:jp_updated_badge_label')}
      tooltipText={t('job-application-page:jp_updated_badge_tooltip')}
      updatedAtLabel={
        lastMaterialUpdateAt
          ? t('job-application-page:jp_updated_at', {
              date: new Date(lastMaterialUpdateAt).toLocaleDateString(),
            })
          : undefined
      }
    />
  ) : null;
  const topStateInfoLabel = React.useMemo(
    () =>
      applicationState === 'rejected' || applicationState === 'not-interested' ? (
        <RejectTopLabel
          title={
            isWithdrawnByWorker
              ? t('job-application-page:withdrawn_label')
              : applicationState === 'not-interested'
              ? t('job-application-page:not_interested')
              : wasSentToEmployer
              ? t('job-application-page:not_successful')
              : t('job-application-page:not_suitable')
          }
        />
      ) : null,
    [t, applicationState, isWithdrawnByWorker, wasSentToEmployer]
  );
  useCurrentApplicationStatus();
  if (applicationState === 'not-found') return <ApplicationNotFound />;
  return (
    <div className="lg:flex lg:gap-[4%] lg:flex-row lg:justify-center">
      <div className="flex flex-col gap-8 lg:max-w-xl">
        <div className="flex w-full px-6 lg:px-0">
          <Button
            StartIcon={ArrowLeftIcon}
            variant="tertiary"
            onClick={() => {
              router.push(`/candidature?category=${jobApplicationCategory}`);
            }}
          />
          <div className="mx-auto">
            <Heading size={5} align="center" className="leading-normal">
              {t('job-application-page:application_status')}
            </Heading>
          </div>
        </div>
        <div className="lg:hidden flex flex-col gap-4 items-center mx-6">
          {topStateInfoLabel}
          {jpUpdatedBadge}
          {isSpontaneousApplication ? <ApplicationInformationCard bgColor="primary" /> : <InfoCardWithToggling />}
        </div>
        <div className="flex justify-start flex-col gap-2 px-6 lg:px-0">
          <Heading size={4} className="leading-7.5">
            {isSpontaneousApplication
              ? t('job-application-page:application_history')
              : isInterviewPending
              ? t('job-application-page:one_step_left')
              : `${t('job-application-page:now_you_can_relax')} 😮‍💨`}
          </Heading>
          <Heading size={5} weight="normal" className="leading-normal">
            {t(
              isSpontaneousApplication
                ? 'job-application-page:keep_everything_under_control_spontaneous'
                : isInterviewPending
                ? 'job-application-page:complete_the_interview_to_move_on'
                : 'job-application-page:keep_everything_under_control'
            )}
          </Heading>
        </div>
        {isSpontaneousApplication ? (
          <div className="px-6">
            <Banner
              title={t('job-application-page:this_is_spontaneous_application')}
              description={t('job-application-page:cannot_update_you_on_their_status_as_they_are_not_traceable')}
            />
          </div>
        ) : null}
        <TimeLineAccordion />
        <ApplicationDiscardButton />
      </div>
      <div className="hidden lg:flex lg:flex-col lg:gap-4 lg:items-center">
        {topStateInfoLabel}
        {jpUpdatedBadge}
        <ApplicationInformationCard bgColor="primary" />
      </div>
    </div>
  );
};
export default JobApplicationDetails;

const RejectTopLabel = ({ title }: { title: string }) => (
  <TopLabelWrapper>
    <Heading size={6} color="error-dark" weight="normal" className="leading-5.25">
      {title}
    </Heading>
  </TopLabelWrapper>
);
const TopLabelWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'min-w-[10rem] px-2 py-1 bg-red-100 text-red-800 rounded-lg justify-center items-center gap-1.5 inline-flex',
      className
    )}
    {...props}
  />
);
/*
// const SpontaneousTopLabel = () => (
//   <TopLabelWrapper>
//     <ClipboardCopyIcon />
//     <Title
//       content="CANDIDATURA SPONTANEA"
//       textColor="bold-purple"
//       fontSize="small"
//       fontWeight="normal"
//       className={'leading-5.25'}
//     />
//   </TopLabelWrapper>
// );
// const ClipboardCopyIcon = () => (
//   <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" fill="none" viewBox="0 0 20 21">
//     <g>
//       <path
//         stroke="#693EB5"
//         strokeLinecap="round"
//         strokeLinejoin="round"
//         strokeWidth="1.5"
//         d="M6.665 4.667H5a1.667 1.667 0 00-1.667 1.666v10A1.666 1.666 0 004.999 18h8.333a1.666 1.666 0 001.667-1.667V15.5M6.665 4.667a1.667 1.667 0 001.667 1.666h1.667a1.667 1.667 0 001.666-1.666m-5 0A1.667 1.667 0 018.332 3h1.667a1.667 1.667 0 011.666 1.667m0 0h1.667a1.666 1.666 0 011.667 1.666v2.5m1.666 3.334H8.332m0 0l2.5-2.5m-2.5 2.5l2.5 2.5"
//       />
//     </g>
//   </svg>
// );*/
const InfoCardWithToggling = () => {
  const { t } = useTranslation();
  return (
    <AccordionV2 className="max-w-sm bg-gradient-base rounded-[15px] w-full" open>
      <AccordionV2.Button className="px-4 py-3.5 w-full text-white">
        <div className="justify-start items-start gap-2 flex">
          <div className="text-white text-base font-medium leading-normal">
            {t('job-application-page:job_position_details')}
          </div>
        </div>
      </AccordionV2.Button>
      <AccordionV2.Panel>
        <ApplicationInformationCard bgColor="none" />
      </AccordionV2.Panel>
    </AccordionV2>
  );
};

const TimeLineAccordion = () => {
  // Built on every render rather than cached in the store. The old cache was
  // written only when the list was empty, so it froze the language and the
  // shape of the timeline at whatever the first render produced.
  const updatedList = useApplicationTimeline();

  return (
    <>
      {[...updatedList].map((data, index) => (
        // Keyed by id, not by index: the list is filtered, so entries are
        // substituted, and AccordionV2 owns local `isOpen` state that React
        // would otherwise hand to a different step.
        <TimeLineAccordionUnit
          isLastIndex={updatedList.length - 1 === index}
          id={data.id}
          key={data.id}
          badge={data.id === 'interview' ? <InterviewStepBadge /> : undefined}
          {...data}
        />
      ))}
    </>
  );
};
/** The same badge the card in the list carries, so the two places read as one thing. */
const InterviewStepBadge = () => {
  const preInterview = usePreInterview();
  return preInterview ? <PreInterviewBadge status={preInterview.status} /> : null;
};

const TimeLineAccordionUnit = ({
  id,
  headText,
  isLastIndex,
  bodyText,
  footerText,
  badge,
}: {
  id: TimeLineKeysType;
  headText: { mainLabel: string; subLabel: string };
  isLastIndex?: boolean;
  bodyText: { mainLabel: string; subLabel?: string; infoLabel?: string };
  footerText?: {
    mainLabel: string;
    href?: string;
  };
  /** State marker beside the step title, visible with the step collapsed. Only the interview has one. */
  badge?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const [activeKey] = useAtom(getActiveKeyAtom);
  const [openKeys] = useAtom(getOpenKeysAtom);
  const [, setOpenKeys] = useAtom(setOpenKeysAtom);
  const [completedKeys] = useAtom(getCompletedKeysAtom);
  const [applicationState] = useAtom(getApplicationStateAtom);
  const [applyJobStatusData] = useAtom(getApplyJobStatusDataAtom);
  const isCompleted = React.useMemo(() => completedKeys.includes(id as TimeLineKeysType), [id, completedKeys]);
  const isOpened = React.useMemo(() => [...openKeys].includes(id), [id, openKeys]);

  const circleIcon = React.useMemo(
    () =>
      isCompleted ? (
        <CheckCircleIcon className="w-5 h-5 text-primary-800" />
      ) : applicationState === 'rejected' || applicationState === 'not-interested' ? (
        <XCircleIcon className="h-5 w-5 rounded-full bg-red-200 text-red-600" />
      ) : activeKey === id ? (
        <div className="w-5 h-5 rounded-full border border-dashed border-white" />
      ) : (
        <div className="w-5 h-5 bg-gray-100 rounded-full border border-gray-300" />
      ),
    [activeKey, applicationState, id, isCompleted]
  );

  return (
    <AccordionV2 className="lg:max-w-lg xl:max-w-3xl" key={id} open={isOpened}>
      <TimelineAccordionButton
        onClick={() => {
          setOpenKeys(isOpened ? openKeys.filter((key) => key !== id) : [...openKeys, id]);
        }}
        $isActiveKey={activeKey === id}
      >
        <div className="flex gap-1 flex-row relative w-full">
          <div className="mt-0.5">{circleIcon}</div>
          <div className="relative w-full">
            <div className="flex flex-col gap-1 text-start lg:flex-row lg:items-center lg:justify-between lg:w-full">
              {/* Title and badge on one line. `flex-wrap` plus `shrink-0` is what keeps the
                  badge readable on a phone: when the two no longer fit side by side the badge
                  drops whole onto the next line, instead of being squeezed until its label
                  wraps mid-phrase or gets cut. */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <div className="text-base font-semibold leading-normal">{headText.mainLabel}</div>
                {badge ? <div className="shrink-0">{badge}</div> : null}
              </div>
              <div className="text-xs font-normal leading-[18px] lg:pr-3">{headText.subLabel}</div>
            </div>
            {!isLastIndex && !isOpened && activeKey !== id ? <HorizontalLineStyled /> : null}
          </div>
          {!isLastIndex && !isOpened && activeKey !== id ? <VerticalLineStyled isCompleted={isCompleted} /> : null}
        </div>
      </TimelineAccordionButton>
      <AccordionV2.Panel>
        <AccordionContentWrapperStyled $isActiveKey={activeKey === id} isLastIndex={isLastIndex}>
          <div className="flex gap-4 flex-col px-3.5">
            {!!bodyText.infoLabel && (
              <Alert
                text={bodyText.infoLabel}
                variant="info"
                className="items-center"
                textClassName="grow shrink basis-0 text-justify text-sm font-normal leading-5.25"
              />
            )}
            <div className="text-gray-600 text-base font-normal leading-normal">
              {bodyText.mainLabel} <br />
              {applyJobStatusData?.status_key === 'worker_jp_employer_interested'
                ? t('job-application-page:employer_interested')
                : null}
              {bodyText.subLabel ? bodyText.subLabel : null}
              {id === 'not-interested' ? <ApplicationReinstate /> : null}
            </div>
            {id === 'interview' ? <InterviewStepAction /> : null}
            {footerText?.href ? <ExternalResourceCard title={footerText.mainLabel} href={footerText.href} /> : null}
            {!isLastIndex ? <hr className="max-w-[100%] mt-6" /> : null}
          </div>
        </AccordionContentWrapperStyled>
      </AccordionV2.Panel>
    </AccordionV2>
  );
};

/**
 * What the worker can do about the interview from here: open the chat while it is pending,
 * reread it once it is closed. An expired interview nobody ever wrote in has no link, because
 * there is nothing to reread.
 *
 * The button says "Completa l'intervista" whether or not the worker already started answering:
 * one label for the one thing left to do (Andrea's review, 18/09).
 */
const InterviewStepAction = () => {
  const { t } = useTranslation();
  const preInterview = usePreInterview();
  if (!preInterview) return null;

  if (preInterview.status === 'pending') {
    return preInterview.href ? (
      <LinkBase link={preInterview.href} newTab={false} twStyle="w-full lg:w-fit">
        <Button variant="primary" fullwidth className="lg:w-fit">
          {t('job-application-page:complete_pre_interview')}
        </Button>
      </LinkBase>
    ) : null;
  }

  return preInterview.transcriptHref ? (
    <LinkBase link={preInterview.transcriptHref} newTab={false} twStyle="w-full lg:w-fit">
      <Button variant="ghost" fullwidth className="lg:w-fit">
        {t('job-application-page:reread_pre_interview')}
      </Button>
    </LinkBase>
  ) : null;
};

const HorizontalLineStyled = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <hr className={cn('w-full absolute -bottom-4 bg-gray-300', className)} {...props} />
);
const VerticalLineStyled = ({
  isCompleted,
  className,
  ...props
}: { isCompleted?: boolean } & React.HTMLAttributes<HTMLElement>) => (
  <span
    className={cn('h-full absolute w-[2px] left-2.5 top-7 bg-gray-300', isCompleted && 'bg-primary-800', className)}
    {...props}
  />
);

const AccordionContentWrapperStyled = ({
  $isActiveKey,
  isLastIndex,
  className,
  ...props
}: { $isActiveKey?: boolean; isLastIndex?: boolean } & React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'mx-8 mt-4 border-l-2',
      $isActiveKey ? 'border-l-primary-300' : 'border-l-gray-200 border-dashed',
      isLastIndex && 'border-l-0',
      className
    )}
    {...props}
  />
);
const TimelineAccordionButton = ({
  $isActiveKey,
  className,
  ...props
}: { $isActiveKey?: boolean } & React.HTMLAttributes<HTMLElement>) => (
  <AccordionV2.Button
    className={cn(
      // Rounded at every width, not only from lg: on a phone the steps were square slabs. The
      // layout already insets the column by 24px, so the corner has room to show.
      'px-6 py-3 w-full rounded-xl',
      $isActiveKey ? 'bg-primary-800 text-white' : 'bg-white text-black',
      className
    )}
    {...props}
  />
);
const ApplicationReinstate = () => {
  const { show: showSuccessModal, open: openSuccessModal, close: closeSuccessModal } = useModal();
  const { onApplicationReInstate, applicationReinstateLoading } = useApplicationReinstate({
    onSuccess: () => openSuccessModal(),
  });
  const router = useRouter();
  const { t } = useTranslation();
  return (
    <>
      <div className="flex flex-col gap-4 py-4">
        <Heading size={5} className="leading-normal">
          {t('job-application-page:changed_your_mind_about_the_job')}
        </Heading>
        <Button
          variant="primary"
          className={'w-full lg:px-10 lg:py-2.5 lg:w-fit'}
          onClick={onApplicationReInstate}
          loading={applicationReinstateLoading}
        >
          {t('common:apply_now')}
        </Button>
      </div>
      <Modal open={showSuccessModal} onOpenChange={(v) => !v && closeSuccessModal()}>
        <ModalContent className="w-full md:max-w-[26rem] md:min-w-[22rem]">
          <ModalConfirmContent
            icon={<CheckCircleIcon className="h-10 w-10 text-green-500" />}
            title={t('common:application_delivered')}
            subtitle={t('common:confirm_if_everything_updated')}
            primaryAction={{
              label: t('common:check_your_profile'),
              onClick() {
                closeSuccessModal();
                router.push('/profilo');
              },
            }}
            secondaryAction={{
              label: t('common:close'),
              onClick() {
                closeSuccessModal();
              },
            }}
          />
        </ModalContent>
      </Modal>
    </>
  );
};
const ApplicationNotFound = () => {
  const router = useRouter();
  const { t } = useTranslation();
  return (
    <JobNotFoundPage
      title={t('job-application-page:no_application_for_the_job')}
      subTitle={t('job-application-page:no_longer_active')}
      primaryButtonProps={{
        title: t('job-application-page:no_longer_active'),
        onClick: () => {
          router.push('/candidature');
        },
      }}
      secondaryButtonProps={{
        title: t('job-application-page:discover_other_positions'),
        onClick() {
          router.push('/cerco-lavoro');
        },
      }}
      icon={<Image width={320} height={324} src={getAssetSrc('/utils-assets/space-discovery.svg')} alt="" />}
    />
  );
};
